import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import AdminManagement from '@/components/superAdminPage/SuperAdminManagemnt'
import { Users, ArrowLeftCircle, LucideIcon, Menu, X } from 'lucide-react'
import { createFileRoute, Outlet, useSearch } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router'
import { DataStorageSuperAdminTour } from '@/tour/superAdminTour/datastorgeSuperAdminTour'
import { guardSuperAdminOnly } from '@/lib/auth/routeGuards'
// import { useSuperAdminStore } from '@/store/superAdminStore'
// import { Button } from '@/components/ui/button'


// Define interfaces for type safety
interface NavItem {
  label: string
  component: string
  icon: LucideIcon
}

interface SearchParams {
  section?: string
}

// Define the navigation items
const navItems: NavItem[] = [
  {
    label: 'Admin Management',
    component: 'AdminManagement',
    icon: Users,
  },
]

// Define the route for the current view
export const Route = createFileRoute('/super_admin/database')({
  beforeLoad: guardSuperAdminOnly,
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return {
      section: search.section as string | undefined,
    }
  },
})

export default function RouteComponent() {
  const [activeComponent, setActiveComponent] = useState('AdminManagement')
  const [activeSubComponent, setActiveSubComponent] = useState('')
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const navigate = useNavigate({ from: '/super_admin/database' })
  const search = useSearch({ from: '/super_admin/database' }) as SearchParams
  // const { startAdminTour } = useSuperAdminStore()

  // Check if there's a section in the URL and update the active components accordingly
  useEffect(() => {
    if (search.section) {
      // If it's a top-level component
      setActiveComponent(search.section)
      setActiveSubComponent('')
    } else {
      // Default to Overview if no section is specified
      setActiveComponent('AdminManagement')
      setActiveSubComponent('')
    }
  }, [search.section])

  // Component mapping
  const componentsMap: { [key: string]: JSX.Element } = {
    AdminManagement: <AdminManagement />,
  }

  // Function to go back to the previous route
  function goBack() {
    navigate({ to: '/super_admin' })
    setIsMobileMenuOpen(false)
  }

  const handleNavClick = (component: string) => {
    setActiveComponent(component)
    setActiveSubComponent('')
    setIsMobileMenuOpen(false)
    navigate({
      search: {
        section: component,
      },
    })
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen">
      {/* Mobile Navbar */}
      <div className="lg:hidden bg-gray-100 border-b border-gray-200 sticky top-0 z-50">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={goBack}
            className="flex items-center gap-2 p-2 rounded-lg text-gray-700 hover:bg-gray-200 hover:text-gray-900 font-medium"
          >
            <ArrowLeftCircle className="h-5 w-5" />
            <span className="hidden sm:inline">Back</span>
          </button>
          <h1 className="text-lg sm:text-xl font-bold">Data Storage</h1>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg text-gray-700 hover:bg-gray-200"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="border-t border-gray-200 bg-white">
            <div className="p-4 space-y-2">
              {navItems.map(({ label, component, icon: Icon }) => (
                <button
                  key={component}
                  onClick={() => handleNavClick(component)}
                  className={cn(
                    'flex items-center gap-2 p-3 rounded-lg w-full text-left font-medium transition-colors',
                    activeComponent === component
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Desktop Sidebar */}
      <Sidebar
        setActiveComponent={setActiveComponent}
        setActiveSubComponent={setActiveSubComponent}
        goBack={goBack}
        activeComponent={activeComponent}
        activeSubComponent={activeSubComponent}
        navigate={navigate}
      />

      {/* Main View - Dynamically rendered components */}
      <div className="flex-1 p-4 sm:p-6 data-storage-header">
        <div className="hidden lg:flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Data Storage</h1>
        </div>
        {componentsMap[activeComponent]}
        <Outlet />
        <DataStorageSuperAdminTour />
      </div>
    </div>
  )
}

// Sidebar Component (Desktop only)
function Sidebar({
  setActiveComponent,
  setActiveSubComponent,
  goBack,
  activeComponent,
  navigate,
}: {
  setActiveComponent: (component: string) => void
  setActiveSubComponent: (component: string) => void
  goBack: () => void
  activeComponent: string
  activeSubComponent: string
  navigate: any
}) {
  return (
    <div
      className={cn(
        'hidden lg:flex h-screen w-64 bg-gray-100 border-r border-gray-200 flex-col'
      )}
    >
      <div className="p-4 space-y-4">
        {/* Back Button with improved design */}
        <button
          onClick={goBack}
          className="flex items-center gap-2 p-2 rounded-lg text-gray-700 hover:bg-gray-200 hover:text-gray-900 w-full text-left font-medium"
        >
          <ArrowLeftCircle className="h-5 w-5 text-gray-700" />
          <span>Back</span>
        </button>

        {/* Navigation items */}
        {navItems.map(({ label, component, icon: Icon }) => (
          <div key={component}>
            <button
              onClick={() => {
                setActiveComponent(component)
                setActiveSubComponent('')
                // Update the URL to reflect the change
                navigate({
                  search: {
                    section: component,
                  },
                })
              }}
              className={cn(
                'flex items-center gap-2 p-2 rounded-lg w-full text-left font-medium',
                activeComponent === component
                  ? 'bg-primary text-white'
                  : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
              )}
            >
              <Icon className="h-5 w-5" />
              <span>{label}</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
